/**
 * The cruise hum (M3 plan R11; `LOOP.reachCruise`): on while cruising or riding a trade lane, its gain, playback
 * rate and low-pass following the travel speed on a log scale (a 1 km/s crawl and a 2×10¹⁰ m/s dash both read).
 * Off while charging (the charge riser owns that), in the relay tunnel (the swell owns it) and in normal flight.
 * Pure and allocation-free.
 */
import type { TravelState } from '../../reach/runtime/types'
import type { CruiseAudioConfig } from './config'

export interface CruiseLoopParams {
  on: boolean
  gain: number
  rate: number
  cutoff: number
}

export function createCruiseLoopParams(): CruiseLoopParams {
  return { on: false, gain: 0, rate: 1, cutoff: 20000 }
}

/** 0 at `speedMin` (or slower), 1 at `speedMax` (or faster), log-interpolated between. */
export function cruiseSpeedFraction(speed: number, cfg: CruiseAudioConfig): number {
  if (!(speed > cfg.speedMin)) return 0
  if (speed >= cfg.speedMax) return 1
  return Math.log(speed / cfg.speedMin) / Math.log(cfg.speedMax / cfg.speedMin)
}

/** Fills `out` from the travel state. */
export function cruiseLoop(travel: TravelState, cfg: CruiseAudioConfig, out: CruiseLoopParams): CruiseLoopParams {
  const lane = travel.mode === 'lane'
  out.on = lane || travel.mode === 'cruise'
  const u = cruiseSpeedFraction(travel.speed, cfg)
  out.gain = (cfg.gainMin + (cfg.gainMax - cfg.gainMin) * u) * (lane ? cfg.laneGain : 1)
  out.rate = cfg.rateMin + (cfg.rateMax - cfg.rateMin) * u
  out.cutoff = cfg.cutoffMin * Math.pow(cfg.cutoffMax / cfg.cutoffMin, u)
  return out
}
